import { Upload as TusUpload } from 'tus-js-client';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { getCurrentInstance } from 'vue';
import { computeErrorMessage } from '@/common';

export type TusUploadItem = {
    id: string;
    file: File | Blob;
    finished: boolean;
    bytesUploaded?: number;
    bytesTotal?: number;
    error?: Error;
    tus?: TusUpload;
    startedAt: Dayjs;
    finishedAt?: Dayjs;
    tusFileId?: string;
    url?: string;
};

export const useTusUploads = () => {
    const uploads = reactive<Record<string, TusUploadItem>>({});
    const clientStore = useClientStore();

    /**
     * Extract the TUS file ID from the URL returned by the TUS server
     */
    const extractTusFileIdFromUrl = (url: string): string | undefined => {
        const match = url.match(/\/tus\/([^/]+)$/);
        return match ? match[1] : undefined;
    };

    const findUpload = (uploadIdOrTusFileId: string): TusUploadItem | undefined => {
        return (
            uploads[uploadIdOrTusFileId] ||
            Object.values(uploads).find(upload => upload.tusFileId === uploadIdOrTusFileId)
        );
    };

    const uploading = computed(() => Object.values(uploads).some(upload => !upload.finished && !upload.error));
    const uploadError = computed(() => {
        const failedUploads = Object.values(uploads).filter(upload => upload.error);
        return failedUploads.length > 0 ? failedUploads[0].error : undefined;
    });

    const uploadProgress = computed(() => {
        const activeUploads = Object.values(uploads).filter(upload => !upload.finished && !upload.error);
        if (activeUploads.length === 0) return undefined;

        const totalBytes = activeUploads.reduce((sum, upload) => sum + (upload.bytesTotal || 0), 0);
        const uploadedBytes = activeUploads.reduce((sum, upload) => sum + (upload.bytesUploaded || 0), 0);

        return totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : undefined;
    });

    const successfulTusItems = computed(() =>
        Object.values(uploads).filter(upload => upload.finished && upload.tusFileId),
    );

    const successfulTusIds = computed(() => successfulTusItems.value.map(upload => upload.tusFileId!));

    const uploadingItems = ref<{ name: string; url: string }[]>();

    /**
     * Start TUS uploads for multiple files
     */
    const startUploads = async (
        files: File[],
        options?: {
            chunkSize?: number;
            retryDelays?: number[];
        },
    ) => {
        try {
            clearAllErrors();
            uploadingItems.value = files.map(f => ({ name: f.name, url: URL.createObjectURL(f) }));

            const uploadPromises = files.map(file => startSingleUpload(file, options));
            await Promise.all(uploadPromises);
        } finally {
            for (const item of uploadingItems.value ?? []) {
                URL.revokeObjectURL(item.url);
            }
            uploadingItems.value = undefined;
        }
    };

    /**
     * Start a single TUS upload
     */
    const startSingleUpload = async (
        file: File,
        options?: {
            chunkSize?: number;
            retryDelays?: number[];
        },
    ): Promise<string> => {
        const uploadId =
            crypto.randomUUID?.() ??
            `${1e7}-${1e3}-${4e3}-${8e3}-${3e11}`.replace(/[018]/g, c =>
                (Number(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))).toString(16),
            );

        const updateUploadStatus = (patch: Partial<TusUploadItem>) => {
            uploads[uploadId] = { ...uploads[uploadId], ...patch };
        };

        if (!clientStore.auth.jwt) {
            throw new Error('User is not logged in.');
        }

        // Construct TUS endpoint URL — the TUS server route is at /tus (not under /api)
        const apiUrl = (import.meta.env.VITE_API_URL || window.location.origin).replace(/\/+$/, '');
        const baseUrl = apiUrl.replace(/\/api$/, '');
        const tusEndpoint = `${baseUrl}/tus`;

        const tus = new TusUpload(file, {
            endpoint: tusEndpoint,
            retryDelays: options?.retryDelays ?? [500, 3000, 5000],
            storeFingerprintForResuming: false,
            removeFingerprintOnSuccess: true,
            fingerprint: async () => uploadId,
            metadata: {
                filename: file.name,
                filetype: file.type,
                name: file.name,
            },
            headers: {
                Authorization: `Bearer ${clientStore.auth.jwt}`,
            },
            chunkSize: options?.chunkSize ?? 1024 * 1024 * 3, // 3MB default
            onError: error => {
                toast.error('Failed to upload file ' + file.name + ' due to ' + computeErrorMessage(error));
                updateUploadStatus({ error: error, finishedAt: dayjs() });
            },
            onShouldRetry: (_e, num) => num < 3,
            onProgress: (bytesUploaded, bytesTotal) => {
                updateUploadStatus({ bytesUploaded, bytesTotal });
            },
            onSuccess: () => {
                updateUploadStatus({
                    finished: true,
                    finishedAt: dayjs(),
                });
            },
            onAfterResponse: (_req, res) => {
                const location = res.getHeader('Location');
                if (location && typeof location === 'string') {
                    const tusFileId = extractTusFileIdFromUrl(location);
                    if (tusFileId) {
                        updateUploadStatus({ tusFileId });
                    }
                }
            },
        });

        // Check for previous uploads to resume
        const previous = await tus.findPreviousUploads();
        if (previous.length > 0) {
            tus.resumeFromPreviousUpload(previous[0]);
        }

        // Initialize the upload in the store
        updateUploadStatus({
            id: uploadId,
            tus,
            file,
            startedAt: dayjs(),
            bytesTotal: file.size,
            finished: false,
            url: URL.createObjectURL(file),
        });

        tus.start();

        return uploadId;
    };

    /**
     * Cancel and remove an upload
     */
    const removeUpload = async (uploadId: string) => {
        const existing = findUpload(uploadId);
        if (existing?.url) {
            URL.revokeObjectURL(existing.url);
            existing.url = undefined;
        }
        if (existing && !existing.finished && !existing.error) {
            existing.tus?.abort().then(() => delete uploads[existing.id]);
        } else if (existing) {
            delete uploads[existing.id];
        }
    };

    /**
     * Clear all finished uploads from the store
     */
    const clearFinished = () => {
        const ids = Object.values(uploads)
            .filter(upload => upload.finished)
            .map(upload => upload.id);
        for (const id of ids) {
            removeUpload(id);
        }
    };

    const cancelUploads = () => {
        const ids = Object.values(uploads)
            .filter(upload => !upload.finished && !upload.error)
            .map(upload => upload.id);
        for (const id of ids) {
            removeUpload(id);
        }
    };

    const clearAllErrors = () => {
        const ids = Object.values(uploads)
            .filter(upload => upload.error)
            .map(upload => upload.id);
        for (const id of ids) {
            removeUpload(id);
        }
    };

    const clearAll = () => {
        const ids = Object.values(uploads).map(upload => upload.id);
        for (const id of ids) {
            removeUpload(id);
        }
    };

    if (getCurrentInstance()) {
        onUnmounted(() => {
            clearAll();
        });
    }

    return {
        uploads: readonly(uploads),
        uploading,
        uploadError,
        uploadProgress,
        uploadingItems,
        successfulTusItems,
        successfulTusIds,
        startUploads,
        startSingleUpload,
        removeUpload,
        clearFinished,
        cancelUploads,
        findUpload,
        clearAll,
    };
};
